// ============================================================
// システムデータ整合 — API クライアント + バックアップ入出力（2026-07-28）
//
// バックエンド: sys/reconcile/system_api.php
//   plan        … 自然言語 → クエリ仕様 JSON（予約データは送らない）
//   backup_*    … 予約の完全スナップショット（JSON）の保存・一覧・取得
//
// バックアップ方式は JSON（サーバ保存 + 手元DL）。CSV は閲覧用の副産物で、
// **復元には使わない**（detail のネスト構造が潰れるため）。
// ============================================================

import { LINE_SYNC_KEY } from '../../config/instances';
import { getInstance } from '../../config/backend';

const SYSTEM_API_URL = (process.env.REACT_APP_RECONCILE_SYSTEM_API_URL as string)
  || 'https://booking.trailernchu.co.jp/sys/reconcile/system_api.php';

async function callSystemApi(action: string, body: Record<string, any> = {}): Promise<any> {
  const res = await fetch(`${SYSTEM_API_URL}?action=${encodeURIComponent(action)}&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!data) throw new Error(`APIエラー (HTTP ${res.status})`);
  if (!data.success) throw new Error(data.error || 'APIエラー');
  return data;
}

/** 対応者名（監査ログ用）。共用PC運用のため bo_staff を見る（feature-operator-tracking.md） */
export function getOperatorName(): string {
  try {
    const raw = localStorage.getItem('bo_staff');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.name === 'string' ? parsed.name.trim() : '';
  } catch {
    return '';
  }
}

// ============================================================
// plan
// ============================================================

export interface PlanResponse {
  kind?: string;
  message?: string;
  query?: any;
  edit?: any;
}

export async function requestPlan(messages: { role: 'user' | 'assistant'; text: string }[]): Promise<PlanResponse> {
  const data = await callSystemApi('plan', { messages });
  return (data.plan || {}) as PlanResponse;
}

// ============================================================
// backup
// ============================================================

export interface BackupMeta {
  backup_id: string;
  created_at: string;
  label: string;
  source: 'manual' | 'auto_before_edit';
  operator: string;
  instance: string;
  count: number;
  bytes?: number;
}

export interface SaveBackupResult {
  backup_id: string;
  count: number;
  bytes: number;
}

/**
 * 予約の完全スナップショットをサーバに保存する。
 * 一括編集の直前バックアップでは source='auto_before_edit' を使い、
 * **これが成功しない限り編集を実行しない**（安全機構③）。
 */
export async function saveBackup(
  reservations: any[],
  options: { label: string; source: BackupMeta['source']; meta?: Record<string, any> },
): Promise<SaveBackupResult> {
  const data = await callSystemApi('backup_save', {
    reservations,
    label: options.label,
    source: options.source,
    operator: getOperatorName(),
    instance: getInstance(),
    meta: options.meta || {},
  });
  return { backup_id: data.backup_id, count: data.count, bytes: data.bytes };
}

export async function listBackups(): Promise<{ backups: BackupMeta[]; retentionDays: number }> {
  const data = await callSystemApi('backup_list');
  return { backups: data.backups || [], retentionDays: data.retention_days ?? 90 };
}

export async function getBackup(backupId: string): Promise<{ meta: BackupMeta; reservations: any[] }> {
  const data = await callSystemApi('backup_get', { backup_id: backupId });
  return { meta: data.meta, reservations: data.reservations || [] };
}

// ============================================================
// ローカル出力（DL）
// ============================================================

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 復元に使える完全な JSON。これが正 */
export function downloadBackupJson(reservations: any[], meta: Record<string, any>, filename: string): void {
  const payload = JSON.stringify({ meta, reservations }, null, 2);
  triggerDownload(new Blob([payload], { type: 'application/json' }), filename);
}

/** CSV に出す列。閲覧・照合用（detail のネストは潰れるため復元には使えない） */
const CSV_COLUMNS: { key: string; label: string; get: (r: any) => any }[] = [
  { key: 'id', label: '予約ID', get: (r) => r?.id },
  { key: 'contact_code', label: '予約番号', get: (r) => r?.contact_code || r?.detail?.contact_id },
  { key: 'start_date', label: '貸出日時', get: (r) => r?.start_date },
  { key: 'end_date', label: '返却日時', get: (r) => r?.end_date },
  { key: 'status', label: 'ステータス', get: (r) => r?.status },
  { key: 'customer_name', label: '予約者名', get: (r) => r?.customer_name },
  { key: 'customer_tel', label: '電話番号', get: (r) => r?.customer_tel },
  { key: 'customer_email', label: 'メール', get: (r) => r?.customer_email },
  { key: 'car_type', label: '車種', get: (r) => r?.car_type },
  { key: 'vehicle_id', label: '車両ID', get: (r) => r?.vehicle_id },
  { key: 'contact_media', label: '予約経路', get: (r) => r?.contact_media ?? r?.detail?.contact_media },
  { key: 'contact_plan', label: 'プラン', get: (r) => r?.detail?.contact_plan },
  { key: 'base_price', label: '基本料金', get: (r) => r?.detail?.base_price },
  { key: 'option_price', label: 'オプション料金', get: (r) => r?.detail?.option_price },
  { key: 'sum_price', label: '合計金額', get: (r) => r?.detail?.sum_price },
  { key: 'short_memo', label: 'ショートメモ', get: (r) => r?.short_memo },
  { key: 'comment', label: 'コメント', get: (r) => r?.detail?.comment },
];

function csvCell(value: any): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  // Excel が数式として解釈するのを防ぐ（=, +, -, @ 始まり）
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** 閲覧用 CSV（BOM 付き = Excel で文字化けしない） */
export function downloadBackupCsv(reservations: any[], filename: string): void {
  const header = CSV_COLUMNS.map((c) => csvCell(c.label)).join(',');
  const lines = (reservations || []).map((r) => CSV_COLUMNS.map((c) => csvCell(c.get(r))).join(','));
  const csv = `﻿${[header, ...lines].join('\r\n')}\r\n`;
  triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
}

/** ファイル名用のタイムスタンプ */
export function fileStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}
